import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, Trash2, Building2, Globe, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function StatusBadge({ s, approved }) {
  let cls = "bg-oc-subtle text-oc-textSecondary";
  let txt = s;
  if (s === "active") {
    cls = approved ? "oc-status-fair" : "bg-oc-secondary/10 text-oc-secondary";
    txt = approved ? "Actief & goedgekeurd" : "Actief, wachtend op goedkeuring";
  } else if (s === "pending_payment") {
    cls = "bg-oc-subtle text-oc-textSecondary";
    txt = "Wacht op betaling";
  } else if (s === "rejected") {
    cls = "oc-status-excessive";
    txt = "Geweigerd";
  } else if (s === "lapsed") {
    cls = "bg-oc-subtle text-oc-textSecondary";
    txt = "Verlopen";
  }
  return <span className={`text-[11px] uppercase font-semibold tracking-wide px-2 py-1 rounded-md inline-block ${cls}`}>{txt}</span>;
}

export default function AdminBusinesses() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");

  const load = async () => {
    const params = new URLSearchParams({ limit: 100 });
    if (status) params.append("status", status);
    const { data } = await api.get(`/businesses/admin/list?${params.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
  };

  useEffect(() => {
    load();
  }, [status]);

  const approve = async (id) => {
    try {
      await api.post(`/businesses/admin/${id}/approve`, {});
      toast.success("Bedrijf goedgekeurd — verschijnt nu in aanbevelingen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Goedkeuren mislukt");
    }
  };

  const reject = async (id) => {
    const reason = prompt("Reden van weigering (optioneel):", "");
    if (reason === null) return;
    try {
      await api.post(`/businesses/admin/${id}/reject`, { reason });
      toast.success("Bedrijf geweigerd");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weigeren mislukt");
    }
  };

  const remove = async (id) => {
    if (!confirm("Bedrijf permanent verwijderen?")) return;
    try {
      await api.delete(`/businesses/admin/${id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <div className="space-y-7">
      <div>
        <h1 className="font-display font-semibold text-oc-primary text-[34px] leading-tight">Partner-bedrijven</h1>
        <p className="text-oc-textSecondary text-[14px]">{total} bedrijven — beoordeel & beheer B2B-aanmeldingen.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-oc-btn" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="pending_payment">Wacht op betaling</SelectItem>
            <SelectItem value="active">Actief</SelectItem>
            <SelectItem value="rejected">Geweigerd</SelectItem>
            <SelectItem value="lapsed">Verlopen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.id} data-testid={`biz-${b.id}`} className="bg-white border border-oc-border rounded-oc p-6 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <Building2 className="w-5 h-5 text-oc-primary" />
                  <p className="font-display font-semibold text-oc-primary text-[18px]">{b.business_name}</p>
                  <StatusBadge s={b.status} approved={b.approved_by_admin} />
                </div>
                <p className="text-oc-textSecondary text-[13px] mt-1 font-mono">{b.vat_number}</p>
              </div>
              <div className="flex gap-2">
                {b.status === "active" && !b.approved_by_admin && (
                  <Button onClick={() => approve(b.id)} data-testid={`approve-${b.id}`} size="sm" className="rounded-oc-btn bg-oc-fair text-white hover:bg-oc-fair/90">
                    <Check className="w-4 h-4 mr-1" /> Goedkeuren
                  </Button>
                )}
                {b.status !== "rejected" && (
                  <Button onClick={() => reject(b.id)} variant="outline" size="sm" className="rounded-oc-btn">
                    <X className="w-4 h-4 mr-1" /> Weigeren
                  </Button>
                )}
                <Button onClick={() => remove(b.id)} variant="ghost" size="sm" className="text-oc-excessive hover:text-oc-excessive hover:bg-oc-excessive/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-oc-textPrimary text-[14px] leading-relaxed">{b.description}</p>
            <div className="grid sm:grid-cols-2 gap-3 text-[13px] text-oc-textSecondary">
              <p className="flex items-center gap-2"><Mail className="w-4 h-4" /><a className="text-oc-secondary underline" href={`mailto:${b.contact_email}`}>{b.contact_email}</a></p>
              {b.website && <p className="flex items-center gap-2"><Globe className="w-4 h-4" /><a className="text-oc-secondary underline" href={b.website} target="_blank" rel="noreferrer">{b.website}</a></p>}
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-oc-border">
              {(b.branches || []).map((br) => (
                <span key={br} className="text-[11px] uppercase font-semibold tracking-wide px-2 py-1 rounded-md bg-oc-primary/10 text-oc-primary">{br}</span>
              ))}
              {(b.regions || []).map((r) => (
                <span key={r} className="text-[11px] font-semibold tracking-wide px-2 py-1 rounded-md bg-oc-subtle text-oc-textSecondary flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{r}
                </span>
              ))}
            </div>
            {b.subscribed_until_iso && (
              <p className="text-[12px] text-oc-textSecondary">Geabonneerd tot: <strong>{new Date(b.subscribed_until_iso).toLocaleDateString("nl-BE")}</strong></p>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-oc-textSecondary py-10">Geen bedrijven gevonden.</p>}
      </div>
    </div>
  );
}
